import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heart, AlertCircle, Download, ArrowRight, CheckCircle } from 'lucide-react';

function FeaturesDemo() {
  const navigate = useNavigate();

  const features = [
    {
      id: 'health-status',
      title: 'Proje Sağlık Durumu',
      icon: <Heart className="h-12 w-12 text-green-600" />,
      description: 'RAG (Red/Amber/Green) göstergeleri ile projelerin sağlık durumunu görüntüleyin',
      color: 'bg-green-50',
      route: '/health-status',
      features: [
        'Yeşil/Sarı/Kırmızı durum göstergeleri',
        'Geciken ve engellenen görev takibi',
        'Tamamlanma yüzdesi',
        'Risk altındaki projeler',
        'Geçmiş sağlık verileri'
      ]
    },
    {
      id: 'task-aging',
      title: 'Görev Yaşlandırma',
      icon: <AlertCircle className="h-12 w-12 text-orange-600" />,
      description: 'Bekleyen görevleri yaşlandırma seviyelerine göre takip edin',
      color: 'bg-orange-50',
      route: '/task-aging',
      features: [
        'Normal, Uyarı, Kritik, Bayat seviyeleri',
        'Yapılandırılabilir eşik değerleri (3/7/14 gün)',
        'Proje ve kişi bazında dağılım',
        'Filtreleme ve sıralama',
        'Otomatik uyarı bildirimleri'
      ]
    },
    {
      id: 'export',
      title: 'Rapor Dışa Aktarma',
      icon: <Download className="h-12 w-12 text-blue-600" />,
      description: 'Raporları PDF ve CSV formatında dışa aktarın',
      color: 'bg-blue-50',
      route: '/export',
      features: [
        'Görev listesi raporu',
        'Proje özeti raporu',
        'Sağlık durumu raporu',
        'Zaman çizelgesi raporu',
        'CSV ve PDF format desteği'
      ]
    }
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                🎉 Yeni Özellikler Demo
              </h1>
              <p className="mt-2 text-gray-600">
                Projenize eklenen yeni özellikleri keşfedin
              </p>
            </div>
            <Button onClick={() => navigate('/')} variant="outline">
              Ana Sayfa'ya Dön
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {features.map((feature) => (
            <Card key={feature.id} className="hover:shadow-2xl transition-shadow">
              <CardHeader className={feature.color}>
                <div className="flex justify-center mb-4">
                  {feature.icon}
                </div>
                <CardTitle className="text-center text-xl">
                  {feature.title}
                </CardTitle>
                <CardDescription className="text-center">
                  {feature.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <ul className="space-y-3 mb-6">
                  {feature.features.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => navigate(feature.route)}
                  className="w-full"
                  style={{ background: '#3B82F6' }}
                >
                  Özelliği Dene
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Stats */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle>📊 Eklenmiş İstatistikler</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-blue-600">3</div>
                <div className="text-sm text-gray-600 mt-2">Yeni Sayfa</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-green-600">15+</div>
                <div className="text-sm text-gray-600 mt-2">API Endpoint</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-600">10+</div>
                <div className="text-sm text-gray-600 mt-2">React Component</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-purple-600">30+</div>
                <div className="text-sm text-gray-600 mt-2">Otomatik Test</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How to Access */}
        <Card className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
          <CardHeader>
            <CardTitle>🚀 Nasıl Erişilir?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Dashboard Header Butonları</h3>
                  <p className="text-gray-600">
                    Ana dashboard'unuzda header'da <span className="font-mono bg-white px-2 py-1 rounded">💚 Sağlık</span>,
                    <span className="font-mono bg-white px-2 py-1 rounded ml-1">⚠️ Yaşlandırma</span>,
                    <span className="font-mono bg-white px-2 py-1 rounded ml-1">📥 Export</span> butonlarını göreceksiniz
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Mobil Menüden</h3>
                  <p className="text-gray-600">
                    Mobil cihazlarda menü butonuna tıklayıp yeni özelliklere erişebilirsiniz
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Direkt URL'ler</h3>
                  <p className="text-gray-600">
                    <code className="bg-white px-2 py-1 rounded">/health-status</code>,
                    <code className="bg-white px-2 py-1 rounded ml-1">/task-aging</code>,
                    <code className="bg-white px-2 py-1 rounded ml-1">/export</code>
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Backend APIs */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>🔌 Backend API Endpoint'leri</CardTitle>
            <CardDescription>Otomatik olarak eklenen API'ler</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">💚 Health Status API</h3>
                <div className="bg-gray-50 p-3 rounded font-mono text-sm space-y-1">
                  <div>GET /api/health/projects/{'{'}project_id{'}'}</div>
                  <div>GET /api/health/summary</div>
                  <div>GET /api/health/dashboard</div>
                  <div>PUT /api/health/projects/{'{'}project_id{'}'}</div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">⚠️ Task Aging API</h3>
                <div className="bg-gray-50 p-3 rounded font-mono text-sm space-y-1">
                  <div>GET /api/exports/aging/config</div>
                  <div>GET /api/exports/aging/tasks</div>
                  <div>GET /api/exports/aging/summary</div>
                  <div>PUT /api/exports/aging/config</div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">📥 Export API</h3>
                <div className="bg-gray-50 p-3 rounded font-mono text-sm">
                  POST /api/exports/generate
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default FeaturesDemo;
